import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Panel } from "@/components/ui/Panel";
import { Field, FormGrid, Input, SaveBar, Textarea } from "@/components/ui/Form";
import { ErrorState, Skeleton } from "@/components/ui/states";
import { friendlyError } from "@/lib/errors";
import { comoTexto, texto, validarAjustes, type StoreSettings } from "./storeApi";
import { useSettingsForm } from "./useSettingsForm";

const desdeAjustes = (a: StoreSettings) => ({
  store_name: comoTexto(a.store_name),
  tagline: comoTexto(a.tagline),
  whatsapp: comoTexto(a.whatsapp),
  phone: comoTexto(a.phone),
  email: comoTexto(a.email),
  address: comoTexto(a.address),
  district: comoTexto(a.district),
  province: comoTexto(a.province),
  department: comoTexto(a.department),
  business_hours: comoTexto(a.business_hours),
  primary_color: comoTexto(a.primary_color),
  secondary_color: comoTexto(a.secondary_color),
  accent_color: comoTexto(a.accent_color),
  seo_title: comoTexto(a.seo_title),
  seo_description: comoTexto(a.seo_description),
});

/** Lo que un comprador ve escrito antes de decidir si confía en la tienda. */
export function IdentityPage() {
  const { consulta, borrador, set, sucio, guardando, guardar, descartar } =
    useSettingsForm(desdeAjustes);
  const [errores, setErrores] = useState<Record<string, string>>({});

  if (consulta.isPending || !borrador) return <Skeleton className="h-96 w-full rounded-lg" />;
  if (consulta.isError) {
    return (
      <ErrorState
        message={friendlyError(consulta.error)}
        onRetry={() => void consulta.refetch()}
      />
    );
  }

  // Los datos que salen en el checkout y en el correo del cliente. Si estos
  // siguen de relleno, la tienda no se puede publicar.
  const sinRellenar = [
    !borrador.whatsapp.trim() && "WhatsApp",
    !borrador.address.trim() && "dirección",
    /placeholder/i.test(borrador.address) && "dirección",
  ].filter(Boolean) as string[];

  async function onGuardar() {
    const fallos = validarAjustes(borrador!);
    setErrores(fallos);
    if (Object.keys(fallos).length > 0) return;

    await guardar({
      store_name: borrador!.store_name.trim(),
      tagline: texto(borrador!.tagline),
      whatsapp: texto(borrador!.whatsapp),
      phone: texto(borrador!.phone),
      email: texto(borrador!.email),
      address: texto(borrador!.address),
      district: texto(borrador!.district),
      province: texto(borrador!.province),
      department: texto(borrador!.department),
      business_hours: texto(borrador!.business_hours),
      // Los colores tienen valor por defecto en la base y no admiten null:
      // dejar el campo vacio significa "no lo cambies", no "borralo".
      ...(borrador!.primary_color.trim() && { primary_color: borrador!.primary_color.trim() }),
      ...(borrador!.secondary_color.trim() && {
        secondary_color: borrador!.secondary_color.trim(),
      }),
      ...(borrador!.accent_color.trim() && { accent_color: borrador!.accent_color.trim() }),
      seo_title: texto(borrador!.seo_title),
      seo_description: texto(borrador!.seo_description),
    });
  }

  return (
    <div className="flex flex-col gap-3">
      {sinRellenar.length > 0 && (
        <div className="flex items-start gap-2.5 rounded-lg border border-warn bg-warn-soft px-4 py-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" />
          <p className="text-meta text-ink">
            Falta completar {[...new Set(sinRellenar)].join(" y ")}. Son datos que el
            comprador ve al pagar y al recoger su pedido.
          </p>
        </div>
      )}

      <Panel title="Identidad">
        <FormGrid>
          <Field label="Nombre de la tienda" required error={errores.store_name}>
            <Input
              value={borrador.store_name}
              onChange={(e) => set("store_name", e.target.value)}
              placeholder="El nombre con el que te conocen"
            />
          </Field>
          <Field label="Lema" hint="La frase corta bajo el logo.">
            <Input
              value={borrador.tagline}
              onChange={(e) => set("tagline", e.target.value)}
              placeholder="Todo para tu mascota"
            />
          </Field>
        </FormGrid>
      </Panel>

      <Panel title="Contacto">
        <FormGrid>
          <Field
            label="WhatsApp"
            hint="Con código de país. Es el botón de contacto de la tienda."
            error={errores.whatsapp}
          >
            <Input
              value={borrador.whatsapp}
              onChange={(e) => set("whatsapp", e.target.value)}
              placeholder="+51 987 654 321"
            />
          </Field>
          <Field label="Teléfono">
            <Input
              value={borrador.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="+51 1 234 5678"
            />
          </Field>
          <Field
            label="Correo"
            hint="Aquí llegan los avisos de pedido nuevo."
            error={errores.email}
          >
            <Input
              value={borrador.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="hola@tudominio.pe"
            />
          </Field>
          <Field label="Horario" hint="Quien recoge en tienda necesita saberlo.">
            <Input
              value={borrador.business_hours}
              onChange={(e) => set("business_hours", e.target.value)}
              placeholder="Lun a Sáb 9:00 - 20:00"
            />
          </Field>
        </FormGrid>
      </Panel>

      <Panel title="Dirección">
        <div className="flex flex-col gap-3.5">
          <Field label="Dirección" hint="Sale en el pedido de quien recoge en tienda.">
            <Input
              value={borrador.address}
              onChange={(e) => set("address", e.target.value)}
              placeholder="Av. La Marina 1234"
            />
          </Field>
          <FormGrid className="sm:grid-cols-3">
            <Field label="Distrito">
              <Input
                value={borrador.district}
                onChange={(e) => set("district", e.target.value)}
                placeholder="San Miguel"
              />
            </Field>
            <Field label="Provincia">
              <Input
                value={borrador.province}
                onChange={(e) => set("province", e.target.value)}
                placeholder="Lima"
              />
            </Field>
            <Field label="Departamento">
              <Input
                value={borrador.department}
                onChange={(e) => set("department", e.target.value)}
                placeholder="Lima"
              />
            </Field>
          </FormGrid>
        </div>
      </Panel>

      <Panel title="Colores">
        <FormGrid className="sm:grid-cols-3">
          <ColorField
            label="Principal"
            value={borrador.primary_color}
            error={errores.primary_color}
            onChange={(v) => set("primary_color", v)}
          />
          <ColorField
            label="Secundario"
            value={borrador.secondary_color}
            error={errores.secondary_color}
            onChange={(v) => set("secondary_color", v)}
          />
          <ColorField
            label="Acento"
            value={borrador.accent_color}
            error={errores.accent_color}
            onChange={(v) => set("accent_color", v)}
          />
        </FormGrid>
      </Panel>

      <Panel title="SEO">
        <div className="flex flex-col gap-3.5">
          <Field label="Título" hint="Lo que sale en Google y al compartir el enlace.">
            <Input
              value={borrador.seo_title}
              onChange={(e) => set("seo_title", e.target.value)}
              placeholder="Tu nombre · lo que haces, en pocas palabras"
            />
          </Field>
          <Field
            label="Descripción"
            hint={`${borrador.seo_description.length} de 160 caracteres recomendados.`}
          >
            <Textarea
              rows={3}
              maxLength={300}
              value={borrador.seo_description}
              onChange={(e) => set("seo_description", e.target.value)}
            />
          </Field>
        </div>
      </Panel>

      <SaveBar
        dirty={sucio}
        saving={guardando}
        onSave={() => void onGuardar()}
        onReset={descartar}
      />
    </div>
  );
}

function ColorField({
  label,
  value,
  error,
  onChange,
}: {
  label: string;
  value: string;
  error?: string;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label} error={error}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="size-9 shrink-0 cursor-pointer rounded-md border border-line bg-surface p-1"
          aria-label={`Elegir color ${label.toLowerCase()}`}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#7c3aed"
          spellCheck={false}
        />
      </div>
    </Field>
  );
}
